// usePolling.js
import { useCallback, useEffect, useRef, useState } from 'react';

export function usePolling(
  callback, // async function to call
  delay = 5000, // normal polling delay in ms
  maxRetries = 5, // max retry attempts on error
  maxDelay = 60000, // max delay for backoff
  condition = true, // condition to run polling
) {
  const savedCallback = useRef();
  const [error, setError] = useState(null);
  const retryCount = useRef(0);
  const timeoutId = useRef(null);

  // Remember latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const clearTimer = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };

  const poll = useCallback(async () => {
    if (!condition) {
      clearTimer();
      return;
    }

    try {
      setError(null);
      await savedCallback.current();
      retryCount.current = 0; // reset retry count on success

      // schedule next poll normally
      timeoutId.current = setTimeout(poll, delay);
    } catch (err) {
      setError(err);

      retryCount.current += 1;
      if (retryCount.current > maxRetries) {
        // stop retrying after maxRetries
        return;
      }

      // exponential backoff delay capped at maxDelay
      const backoffDelay = Math.min(delay * 2 ** retryCount.current, maxDelay);
      timeoutId.current = setTimeout(poll, backoffDelay);
    }
  }, [delay, maxRetries, maxDelay, condition]);

  useEffect(() => {
    if (condition) {
      poll(); // start polling immediately
    }

    return () => {
      clearTimer();
    };
  }, [poll, condition]);

  return { error };
}
